// ─── usePermisos — ¿qué puede hacer el usuario logueado en este negocio? ─────
//
// Mismo espíritu que usePlan(): un hook barato que la UI consulta para
// ocultar/deshabilitar acciones. Fuente de verdad:
//   - user.role (team_members.role, lo pone AuthContext vía getMyRole)
//   - overrides por empleado (team_members.permisos, RPC get_mis_permisos)
//   - overrides por ROL del negocio (businesses.permisos_roles, 0052) —
//     capa 2, lo que el admin decidió que puede cada rol en SU negocio
//   → permisosEfectivos(role, overridesEmpleado, permisosRoles[role])
//
// Reglas especiales:
//   - Super admins de mezo (juaneslizcanom / temetcol) → todo true.
//   - ADMIN → todo true (el dueño nunca se recorta).
//   - role null (cuenta dueña vieja sin team_member, o hidratación aún en
//     curso) → se trata como ADMIN: jamás dejamos a alguien fuera de su
//     propio negocio por un dato faltante.
//
// Los overrides (empleado + roles del negocio) se cargan UNA vez por
// (usuario, negocio) en un store de módulo (no un request por componente) y
// se comparten entre todos los usePermisos() montados vía useSyncExternalStore.
// Con 0052 get_mis_permisos ya trae `permisos_roles` (1 request); si la RPC
// es la vieja (0049) se pide aparte con getPermisosRoles() (→ {} pre-0052).

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMisPermisos, getPermisosRoles } from '../services';
import { getBusinessId } from '../services/supabaseClient';
import {
  permisosEfectivos, permisosRolNegocio, sanearPermisosRoles,
  normalizarRol, TODOS_LOS_PERMISOS, ROLES,
  PERMISO_POR_RUTA, rutaPermitida,
} from '../config/permisos';

// Contrato para el sidebar (DashboardLayout): `rutaPermitida(permisos, path)`
// y el mapa `PERMISO_POR_RUTA`. Viven en config/permisos.js (puros, con test)
// y se re-exportan aquí para que el layout solo importe de este hook.
export { PERMISO_POR_RUTA, rutaPermitida };

// Mismo par de correos que AdminMezo.jsx / emails.js (ADMINS_MEZO).
const SUPER_ADMINS_MEZO = ['juaneslizcanom@gmail.com', 'temetcol@gmail.com'];

export function esSuperAdminMezo(email) {
  return !!email && SUPER_ADMINS_MEZO.includes(String(email).toLowerCase());
}

// ─── Store de overrides (módulo) ─────────────────────────────────────────────
// `listo` = ya terminó al menos una carga para esta clave. Un refresh en
// segundo plano (volver a la pestaña) pone cargando=true pero conserva
// listo=true → la UI no parpadea ni vuelve al spinner.
let _estado = { clave: null, overrides: {}, permisosRoles: {}, rolServidor: null, cargando: false, listo: false };
const _oyentes = new Set();
let _cargaEnCurso = null;

function _emitir(nuevo) {
  _estado = { ..._estado, ...nuevo };
  _oyentes.forEach((fn) => { try { fn(); } catch { /* noop */ } });
}
function _suscribir(fn) { _oyentes.add(fn); return () => _oyentes.delete(fn); }
function _snapshot() { return _estado; }

function _cargar(clave, forzar = false) {
  if (!clave) return;
  if (!forzar && _estado.clave === clave && !_estado.cargando) return;   // ya en caché
  if (_cargaEnCurso === clave) return;                                   // dedupe
  _cargaEnCurso = clave;
  _emitir({ clave, cargando: true, listo: _estado.clave === clave ? _estado.listo : false });
  getMisPermisos()
    .then(async (r) => {
      // Capa 2: si la RPC ya la trae (0052) no hay segundo request; si no,
      // la pedimos aparte (pre-0052 devuelve {} sin ruido).
      let permisosRoles = (r && typeof r.permisosRoles === 'object' && r.permisosRoles) || null;
      if (!permisosRoles) {
        try { permisosRoles = await getPermisosRoles(); } catch { permisosRoles = {}; }
      }
      return { r, permisosRoles };
    })
    .then(({ r, permisosRoles }) => {
      if (_cargaEnCurso !== clave) return;   // cambió de usuario/negocio mientras cargaba
      _emitir({
        clave,
        overrides:     (r && typeof r.overrides === 'object' && r.overrides) || {},
        permisosRoles: sanearPermisosRoles(permisosRoles),
        rolServidor:   r?.rol ?? null,
        cargando:      false,
        listo:         true,
      });
    })
    .catch(() => {
      if (_cargaEnCurso !== clave) return;
      _emitir({ clave, overrides: {}, permisosRoles: {}, rolServidor: null, cargando: false, listo: true });
    })
    .finally(() => { if (_cargaEnCurso === clave) _cargaEnCurso = null; });
}

// Fuerza recarga de overrides + permisos por rol (p.ej. tras editar permisos
// de un empleado o guardar la pestaña "Roles y permisos" — si el admin se
// editó a sí mismo o para refrescar sin recargar la página).
export function invalidarPermisos() {
  const clave = _estado.clave;
  _emitir({ clave: null, overrides: {}, permisosRoles: {}, rolServidor: null, cargando: false, listo: false });
  if (clave) _cargar(clave, true);
}

// Al volver a la pestaña refrescamos: así un cambio de permisos hecho por el
// admin le llega al empleado sin que cierre sesión.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && _estado.clave) _cargar(_estado.clave, true);
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────
export function usePermisos() {
  const { user } = useAuth();
  const estado   = useSyncExternalStore(_suscribir, _snapshot, _snapshot);

  // Clave de caché: usuario + negocio activo (el business id vive en localStorage).
  const businessId = getBusinessId();
  const clave = user?.id ? `${user.id}|${businessId ?? ''}` : null;

  useEffect(() => { if (clave) _cargar(clave); }, [clave]);

  const email      = user?.email ?? null;
  const rolUsuario = user?.role ?? null;

  return useMemo(() => {
    const superAdmin = esSuperAdminMezo(email);
    const enCache    = clave !== null && estado.clave === clave;
    const rolCrudo   = rolUsuario ?? (enCache ? estado.rolServidor : null);
    // Sin rol conocido → ADMIN (dueño viejo sin team_member / hidratando).
    const rol        = normalizarRol(rolCrudo) ?? 'ADMIN';
    const esAdmin    = superAdmin || rol === 'ADMIN';
    const overrides  = enCache ? estado.overrides : {};
    // Capa 2: lo que el negocio definió por rol ({ CASHIER: {...}, ... }).
    const permisosRoles = enCache ? estado.permisosRoles : {};
    const permisos   = esAdmin ? TODOS_LOS_PERMISOS : permisosEfectivos(rol, overrides, permisosRoles[rol]);
    // Solo "cargando" mientras no tengamos NADA listo para este usuario y el
    // rol no sea admin (el admin no depende de overrides).
    const cargando   = !esAdmin && !!clave && !(enCache && estado.listo);

    const puede = (permiso) => permisos[permiso] === true;

    return {
      rol,                                   // 'ADMIN' | 'CASHIER' | 'WAITER' | 'KITCHEN'
      rolInfo:      ROLES[rol] ?? ROLES.ADMIN,
      esAdmin,
      esSuperAdmin: superAdmin,
      permisos,                              // { 'pos.cobrar': true, ... }
      overrides,
      permisosRoles,                         // overrides por rol del negocio (0052)
      // Permisos de un ROL en este negocio (default mezo + override del
      // negocio, SIN override de persona). Base de la matriz por empleado y
      // de la pestaña "Roles y permisos".
      rolBase:      (r) => permisosRolNegocio(r, permisosRoles[normalizarRol(r)]),
      puede,
      cargando,
      // true cuando el store ya cargó overrides + permisosRoles de ESTE
      // usuario/negocio (el admin no "carga" pero la pestaña de roles sí
      // necesita saber cuándo llegó el mapa del negocio).
      permisosListos: enCache && estado.listo,
      rutaPermitida: (path) => rutaPermitida(permisos, path),
      recargar:      invalidarPermisos,
      // Texto estándar para tooltips de botones deshabilitados.
      motivoBloqueo: 'Tu rol no tiene este permiso',
    };
  }, [email, rolUsuario, clave, estado]);
}

export const MSG_SIN_PERMISO = 'Tu rol no tiene este permiso';
